import { createContext, FC, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { Player } from './player/Player';
import { noop } from './noop';
import { useSelector } from 'react-redux';
import { getCurrentProjectId } from 'modules/projectFles';

export type Listener = (p: Player) => any;
export type PlayerCtx = {
  player: Player;
  addListener: (l: Listener) => void;
  removeListener: (l: Listener) => void;
};

let player = new Player(noop, noop);

let listeners: Listener[] = [];
const addListener = (listener: Listener) => {
  listeners.push(listener);
};
const removeListener = (listener: Listener) => {
  listeners = listeners.filter(item => item !== listener);
};

const ctxData = { player, addListener, removeListener };

export const getPlayer = () => player;

export const PlayerContext = createContext<PlayerCtx>(ctxData);

export const PlayerContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const projectId = useSelector(getCurrentProjectId);
  useEffect(() => {
    const onUpdate = () => listeners.forEach(item => item(player));

    player.destruct();
    player = new Player(onUpdate, onUpdate);
    return () => player.destruct();
  }, [projectId]);

  return useMemo(
    () => <PlayerContext.Provider value={ctxData}>{children}</PlayerContext.Provider>,
    [children]
  );
};

export const usePlayer = (selector: (p: Player) => any = (p: Player) => p) => {
  const [val, setVal] = useState(selector(player));
  const { addListener, removeListener } = useContext(PlayerContext);

  useEffect(() => {
    const foo = (value: Player) => {
      setVal(selector(value));
    };
    addListener(foo);
    return () => removeListener(foo);
  }, [addListener, removeListener, selector]);

  return val;
};

export const playingSelector = (p: Player) => p.playing;
export const timeSelector = (p: Player) => p.time;
